import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { useFileRecordTags } from "@/shared/domain/files/hooks/useFileRecordTags";
import { useMutateFileTags } from "@/shared/domain/files/hooks/useMutateFileTags";
import { Banner } from "@/shared/foundation/primitives";

import { TagsEditor } from "../TagsEditor";

import { TagsSkeleton } from "./TagsSkeleton";

interface UpdateFileTagsProps {
  fileId: string;
  /** Org id of the file being mutated; threaded into cache invalidation. */
  resourceOwnerId: string;
  readonly?: boolean;
}

/**
 * A file's tags in the shared {@link TagsEditor}, loaded on their own and
 * saved one at a time with put/remove tags.
 */
export const UpdateFileTags: React.FC<UpdateFileTagsProps> = ({
  fileId,
  resourceOwnerId,
  readonly = false,
}) => {
  const fileTagsQuery = useFileRecordTags(fileId);
  const { mutateAsync: mutateFileTags } = useMutateFileTags();

  if (fileTagsQuery.isLoading) {
    return <TagsSkeleton />;
  }

  if (fileTagsQuery.isError) {
    return <Banner variant="error" text={fileTagsQuery.error.message} />;
  }

  return (
    <TagsEditor
      tags={fileTagsQuery.data ?? []}
      recordKey={fileId}
      readonly={readonly}
      autocompleteType={AutocompleteType.FileTags}
      onAdd={(tag) =>
        mutateFileTags({ fileId, resourceOwnerId, putTags: [tag] })
      }
      onRemove={(tag) =>
        mutateFileTags({ fileId, resourceOwnerId, removeTags: [tag] })
      }
      analytics={{
        add: "AddFileTagClicked",
        remove: "RemoveFileTagClicked",
        props: { fileId },
      }}
    />
  );
};
